<template>
  <div class="accordion-objectives-container">
    <template v-if="fetching">
      <p class="text-muted gb-text-1"><i class="fa fa-circle-o-notch fa-spin"/> Loading Objectives...</p>
    </template>
    <AccordionObjective
      v-for="(obj, index) in objectives"
      :key="obj.level + '-' + obj.uuid"
      :level="obj.level"
      :title="obj.title"
      :text="levelledObjectiveTexts[index]"
      :access-info="accessInfo"
      :print-permissions="printPermissions"
      :page-uuid="pageUuid"
    >
      <CopyTrackWrapper
        :page-uuid="pageUuid"
        :level="obj.level"
        type="objective"
      >
        <EditableWikiTextArea
          class="acc-obj-editable-textarea"
          v-if="editable && levelledObjectiveTexts[index]"
          v-model="levelledObjectiveTexts[index]"
          :strategies="strategyMap"
          copy-tooltip-text="Objective Copied"
          :parent-edit-mode.sync="editMode"
          :has-resources="hasResources"
          :textarea-id="'acc-obj-text' + index"
          :show-buttons="hasResources"
          @textEdited="objectiveTextEdited(index, obj)"
          @textReset="objectiveTextEdited(index, obj)"
          :content-title="obj.title"
          :evt-data="{obj_level: obj.level}"
          :object-id="obj.uuid"
        />
        <WikiText
          v-if="!editable && obj.text"
          :text="obj.text"
          :strategies="strategyMap"
          :locked="accessInfo.blur"
        />
        <ResourceLanguageDropdown v-if="!editable && obj.text && hasMultilingualResources(obj.uuid) && obj.uuid" :object-id="obj.uuid" />
      </CopyTrackWrapper>
      <ResourceListContainer
        v-if="obj.resources.length > 0"
        :uuids="obj.resources"
        :show-resource-tile="true"
        :evt-data="{obj_level: obj.level}"
        :object-id="obj.uuid"
      />
      <hr v-if="hasResources && index !== objectives.length - 1">
    </AccordionObjective>

    <CopyTrackWrapper
      level="all"
      title="all"
      text="all"
      type="objective"
      :uuid="pageUuid"
      v-if="!hasResources"
    >
      <button
        class="btn btn-primary btn-xs"
        @click="toggleEditMode(true)"
        v-show="editable && !editMode"><i class="fa fa-edit"/> Edit</button>
      <button
        class="btn btn-success btn-xs"
        @click="toggleEditMode(false)"
        v-show="editable && editMode"><i class="fa fa-save"/> Done</button>
      <CopyButton
        btn-class="btn btn-default btn-xs"
        :copy-text="objectiveCopyText"
        success-text="Copied All Objectives"
        error-text="Error copying text. Use Ctrl+C instead.">
        <i class="fa fa-copy"/> Copy
      </CopyButton>
    </CopyTrackWrapper>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AccordionObjective from '@/components/goals/AccordionObjective.vue'
import EditableWikiTextArea from '@/components/EditableWikiTextArea.vue'
import WikiText from '@/components/WikiText'
import CopyButton from '../CopyButton.vue'
import CopyTrackWrapper from '../CopyTrackWrapper.vue'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import ResourceListContainer from '@/components/ResourceListContainer.vue'
import ResourceLanguageDropdown from '@/components/ResourceLanguageDropdown.vue'

export default {
  name: 'AccordionObjectivesList',
  components: {
    AccordionObjective,
    EditableWikiTextArea,
    WikiText,
    CopyButton,
    CopyTrackWrapper,
    ResourceListContainer,
    ResourceLanguageDropdown
},
  props: {
    objectives: {
      type: Array,
      default() {
        return []
      }
    },
    // this prop is basically just for inclusion in analytics...
    pageUuid: {
      type: String,
      default: ''
    },
    accessInfo: {
      type: Object,
      required: true
    },
    printPermissions: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    levelledObjectiveTexts: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
      return {
        fetching: false,
        editMode: false
      }
  },
  computed: {
    ...mapGetters('goaldetail', ['strategyMap', 'hasNonEnglishLangById']),
    objectiveCopyText() {
      return this.levelledObjectiveTexts.map(text => wikiToPlainText(text)).join('\n\n')
    },
    isTextEdited() {
      return this.objectives.some((obj,i) => {
        return obj.text !== this.levelledObjectiveTexts[i]
      })
    },
    hasResources() {
      return this.objectives.some(obj => obj.resources.length)
    }
  },
  watch: {
    editMode(newMode) {
      if (newMode) {
        this.$sendEvent('Activated Edit Text', {
          obj_level: 'all',
        })
      } else {
        this.$sendEvent('Deactivated Edit Text', {
          obj_level: 'all',
          is_text_edited: this.isTextEdited
        })
      }
    }
  },
  methods: {
    toggleEditMode(mode) {
      this.editMode = mode
    },
    objectiveTextEdited(index) {
      this.$emit('objective:edited', {
        index,
        objective: {
          text: this.levelledObjectiveTexts[index],
          level: this.objectives[index].level,
          title: this.objectives[index].title,
        },
        ...(!this.hasResources && {allObjectives: true})
      })
    },
    hasMultilingualResources(uuid) {
      if (!uuid) return false
      return this.hasNonEnglishLangById(uuid)
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.accordion-objectives-container {
  padding: 0 48px 12px 48px;
}

/deep/ .accordion-objective {
  font-weight: 400;
}

hr {
  color: #dedede;
}
</style>
